import { Injectable } from '@angular/core';
import { ApiService } from 'src/app/core/api.service';
import { environment } from '../../../environments/environment';

import { BaseApiOutput } from 'src/app/shared/models/api/base-api.model';
import { CAccountInfo, ChargeBeeProfileApiOutput } from 'src/app/shared/models/api/chargbeeprofile-api.model';

interface HasScheduledSoloDowngradeApiOutput extends BaseApiOutput {
  has_scheduled_downgrade: number;
}

interface CancelScheduledSoloDowngradeApiOutput extends BaseApiOutput {
  cancelled: number;
}
import { PaymentSuccessType } from 'src/app/shared/models/provision.model';
import { Store } from '@ngrx/store';
import { UserService } from 'src/app/core/user.service';
import * as fromRoot from '../../reducers';
import * as ChargebeeActions from '../../actions/chargebee-actions';
import * as fromChargebee from '../../reducers/chargebee.reducer';
import { LoggerService } from 'src/app/core/logger.service';
import { Router } from '@angular/router';
import { BroadcastService } from './broadcast.service';
import { PermissionActions } from '../models';
import { UrlService } from 'src/app/core/url.service';
import { Observable, Subscription } from 'rxjs';
import { bytesToGB } from '../func';
import { WebAuthService } from '../../auth/services/web-auth.service';
import { SnackbarService } from './snackbar.service';
import { DialogService } from './dialog.service';
import { ConfirmCancelSoloDowngradeDialogComponent } from '../components/confirm-cancel-solo-downgrade-dialog/confirm-cancel-solo-downgrade-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class ChargebeeService {
  public chargebeeProfile: CAccountInfo;
  public userLimit: number;
  private isLoading = false;
  private chargebeeInstance: ChargebeeInstance;

  constructor(
    private apiService: ApiService,
    private store: Store<fromRoot.State>,
    private userService: UserService,
    private logger: LoggerService,
    private router: Router,
    private broadcastService: BroadcastService,
    private readonly urlService: UrlService,
    private webAuthService: WebAuthService,
    private snackbarService: SnackbarService,
    private dialogService: DialogService,
  ) {
    this.broadcastService.on('sync.reset.user').subscribe(() => {
      this.chargebeeProfile = undefined;
    });
  }

  public async gotoHubspotUpgradePage(): Promise<void> {
    this.webAuthService.clearWebAuthData();
    if (!this.userService.isRedirectToUpgradePlan()) {
      this.openPortal();
      return;
    }
    const queryParams = await this.getHotSpotqueryParams();
    this.urlService.gotoHubspotUpgradePage(queryParams);
  }

  public async openPortal(reactivationToken?: string, onError?: (error: unknown) => void): Promise<void> {
    this.webAuthService.clearWebAuthData();
    this.chargebeeInstance = window['Chargebee'].init({
      site: environment.chargebeeSite,
    });
    this.chargebeeInstance.logout();
    this.chargebeeInstance.closeAll();
    if (!reactivationToken && !this.userService.checkPermission(PermissionActions.MANAGE_BILLING)) {
      this.logger.error('User does not have permission to manage billing');
      return;
    }
    try {
      this.chargebeeInstance.setPortalSession(() => {
        return this.apiService
          .execute<any>('chargebeesession', reactivationToken ? { reactivation_token: reactivationToken } : {})
          .then((response) => response.portal_session)
          .catch((ex) => {
            this.chargebeeInstance.closeAll();
            onError?.(ex);
          });
      });
      this.chargebeeInstance.createChargebeePortal().open({
        subscriptionChanged: async (data) => {
          this.chargebeeInstance.closeAll();
          const fromfree = this.userService.get('is_free_plan');
          this.router.navigate(['success'], {
            queryParams: {
              sub_id: data?.subscription?.id,
              type: PaymentSuccessType.UPGRADE,
              user_id: this.userService.get('id'),
              fromfree,
            },
          });
        },
        scheduledCancellationRemoved: async () => {
          this.refreshEntitlements();
          this.logger.info('Scheduled cancellation removed, refreshing entitlements');
        },
        subscriptionReactivated: async (data) => {
          this.chargebeeInstance.closeAll();
          const result = await this.apiService.execute<any>('reactivateaccount', {
            reactivation_token: reactivationToken,
            sub_id: data?.subscription?.id,
          });
          this.logger.info('Subscription reactivated');
          this.router.navigate(['success'], {
            queryParams: {
              sub_id: data?.subscription?.id,
              type: PaymentSuccessType.REACTIVATION,
              user_id: result.user_id,
            },
          });
        },
      });
    } catch (ex) {
      this.logger.e('Failed to open chargebee self-serve portal', ex);
      throw ex;
    }
  }

  public getSubscription(): Observable<fromChargebee.State> {
    if (!this.isLoading && !this.chargebeeProfile) {
      this.isLoading = true;
      if (this.isLoading) {
        this.store.dispatch(new ChargebeeActions.ChargebeeGetAction());
      } else {
        this.store.dispatch(
          new ChargebeeActions.ChargebeeSuccessAction({
            cprofile: undefined,
          }),
        );
      }
    }
    return this.store.select(fromRoot.getChargebeeState);
  }

  public refreshSubscription(): void {
    this.store.dispatch(new ChargebeeActions.ChargebeeGetAction());
  }

  public async getChargebeeProfile(): Promise<{ cprofile: CAccountInfo } | null> {
    const retval = await this.apiService.execute<ChargeBeeProfileApiOutput>('chargebeesubscriptiondetails', {}, true);
    this.userLimit = retval?.cprofile?.subscription?.user_limit || 0;
    return retval.cprofile?.subscription
      ? {
          cprofile: retval?.cprofile,
        }
      : null;
  }

  public refreshEntitlements = async (): Promise<void> => {
    await this.apiService.execute('syncentitlements', {});
    this.refreshSubscription();
    this.userService.refresh();
  };

  public async hasScheduledSoloDowngrade(): Promise<boolean> {
    const result = await this.apiService.execute<HasScheduledSoloDowngradeApiOutput>('hasscheduledsolodowngrade', {});
    return !!result?.has_scheduled_downgrade;
  }

  public async cancelScheduledSoloDowngrade(): Promise<void> {
    await this.apiService.execute<CancelScheduledSoloDowngradeApiOutput>('cancelscheduledsolodowngrade', {});
  }

  // Gate before any action that would void a pending Teams->Solo downgrade.
  // Returns true to proceed; cancels the scheduled change on confirm.
  public async confirmCancelSoloDowngrade(): Promise<boolean> {
    let pending = false;
    try {
      pending = await this.hasScheduledSoloDowngrade();
    } catch {
      return true;
    }
    if (!pending) return true;
    const confirmed = await this.dialogService
      .open(ConfirmCancelSoloDowngradeDialogComponent)
      .afterClosed()
      .toPromise();
    if (!confirmed) return false;
    try {
      await this.cancelScheduledSoloDowngrade();
    } catch (ex) {
      this.logger.e('Failed to cancel scheduled Solo downgrade', ex);
    }
    return true;
  }

  public async chargebeeSubscription(): Promise<CAccountInfo> {
    let chargebeeSubscription!: Subscription;
    const cprofile: CAccountInfo = await new Promise((resolve) => {
      chargebeeSubscription = this.getSubscription().subscribe((data) => {
        if (data.isLoaded) {
          if (chargebeeSubscription) {
            chargebeeSubscription.unsubscribe();
          }
          resolve(data.cprofile);
        }
      });
    });
    return cprofile;
  }
  private async getHotSpotqueryParams(): Promise<Record<string, unknown>> {
    const cprofile: CAccountInfo = await this.chargebeeSubscription();
    let chargebeeCustomerID;
    const isTrial = this.userService.get('is_on_trial') ? 'yes' : 'no';
    let currencyCode;
    const currentStorageUtilizationGB = bytesToGB(this.userService.get('disk_usage_bytes'), 2);
    const currentStorageMaxGB = this.userService.get('is_unlimited_plan')
      ? 'Unlimited'
      : bytesToGB(this.userService.get('disk_limit_bytes'), 0);
    let planName = this.userService.get('plan_name');

    const sku = this.userService.get('plan_sku') || 'free';
    if (cprofile.subscription) {
      planName = cprofile.subscription.planName;
      chargebeeCustomerID = cprofile.subscription.chargebeeCustomerID;
      currencyCode = cprofile.subscription.currencyCode;
    }
    const queryParams = {
      UserID: this.userService.get('id'),
      CBID: chargebeeCustomerID,
      CurrentSKU: sku,
      CurrentPlanName: planName,
      IsTrial: isTrial,
      CurrentStorageUtilizationGB: currentStorageUtilizationGB,
      CurrentStorageMaxGB: currentStorageMaxGB,
      CurrencyCode: currencyCode,
    };
    return queryParams;
  }
}
